import { MEDIA_CACHE } from '../media-cache.js';
import { RESULTS_KEY } from '../engine/result-queue.js';
import { TROUBLE_KEY } from './trouble.js';

/**
 * What a school leaves on a headset, and letting go of it.
 *
 * A headset moves between schools: sold on, lent out, re-paired after a
 * mix-up. When its pairing ends, what belonged to that school must not go
 * with it (docs/06-security-compliance.md, "What a headset keeps"):
 *
 * - the `lessons` cache: the school's lesson list and bodies from the API;
 * - the `media` cache: its photos, including its own 360° photos
 *   (ADR-036), which may show people. Keyed by id with the signature
 *   stripped, so a hit is served without asking the server anything;
 * - results still queued for a session of that school, which can no longer
 *   be delivered and must never be delivered to the next one;
 * - the trouble note, which describes the old pairing, not the new one.
 *
 * What is the same for every school stays: the app shell (precache), the
 * worlds (content-addressed, ADR-032) and this viewer's mute preference.
 *
 * Done from the page with the Cache Storage API. The service worker holds no
 * copy of its own -- Workbox opens a cache by name on every request -- so
 * there is nothing to tell it.
 */

/** The Workbox runtime caches holding a school's data (vite.config.ts). */
export const SCHOOL_CACHES = ['lessons', MEDIA_CACHE] as const;

/** The runtime caches that are the same for every school, kept on purpose. */
export const KEPT_CACHES = ['worlds'] as const;

/** localStorage entries holding a school's data. The token is the pairing's own business. */
export const SCHOOL_KEYS = [RESULTS_KEY, TROUBLE_KEY] as const;

/**
 * Deletes the school's caches and stored entries. Returns what it cleared,
 * as `cache:<name>` and `key:<key>`. Never throws: a cache that cannot be
 * deleted must not stop the rest, nor a headset from pairing again.
 */
export async function forgetSchoolData(
  storage: Pick<CacheStorage, 'delete'> | null | undefined = globalThis.caches,
  local: Pick<Storage, 'removeItem'> | null | undefined = safeLocalStorage(),
): Promise<string[]> {
  const cleared: string[] = [];

  if (storage) {
    for (const name of SCHOOL_CACHES) {
      try {
        if (await storage.delete(name)) cleared.push(`cache:${name}`);
      } catch {
        // Carry on with the rest; see above.
      }
    }
  }

  if (local) {
    for (const key of SCHOOL_KEYS) {
      try {
        local.removeItem(key);
        cleared.push(`key:${key}`);
      } catch {
        // Storage refused; nothing more to do.
      }
    }
  }

  return cleared;
}

function safeLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}
